import { supportedLanguages, type SupportedLanguage } from "./resources";

const supportedLanguageByCode = <T extends Record<string, SupportedLanguage>>(
  locales: T,
): Record<string, SupportedLanguage> => locales;

/**
 * Mapping of legacy one-part locale codes to their two-part equivalents.
 */
const LEGACY_LOCALE_MAP = supportedLanguageByCode({
  en: "en-US",
  fr: "fr-FR",
  es: "es-419", // Default Spanish to Latin American (larger market)
  de: "de-DE",
  da: "da-DK",
  it: "it-IT",
  pt: "pt-BR",
  ja: "ja-JP",
  ko: "ko-KR",
  zh: "zh-CN", // Default Chinese to Simplified
});

/**
 * Mapping of specific regional variants to supported locales.
 * Used for exact matches before falling back to language defaults.
 */
const REGIONAL_VARIANT_MAP = supportedLanguageByCode({
  // English variants
  "en-us": "en-US",
  "en-gb": "en-GB",
  "en-au": "en-GB", // Australia → UK English
  "en-nz": "en-GB", // New Zealand → UK English
  "en-ie": "en-GB", // Ireland → UK English
  "en-ca": "en-US", // Canada English → US English
  "en-sloth": "en-sloth",

  // French variants
  "fr-fr": "fr-FR",
  "fr-ca": "fr-CA",
  "fr-be": "fr-FR", // Belgium → France French
  "fr-ch": "fr-FR", // Switzerland → France French

  // Spanish variants - Americas use es-419, Europe uses es-ES
  "es-es": "es-ES",
  "es-419": "es-419",
  "es-mx": "es-419", // Mexico → Latin American
  "es-ar": "es-419", // Argentina → Latin American
  "es-co": "es-419", // Colombia → Latin American
  "es-cl": "es-419", // Chile → Latin American
  "es-pe": "es-419", // Peru → Latin American
  "es-ve": "es-419", // Venezuela → Latin American
  "es-ec": "es-419", // Ecuador → Latin American
  "es-gt": "es-419", // Guatemala → Latin American
  "es-cu": "es-419", // Cuba → Latin American
  "es-bo": "es-419", // Bolivia → Latin American
  "es-do": "es-419", // Dominican Republic → Latin American
  "es-hn": "es-419", // Honduras → Latin American
  "es-py": "es-419", // Paraguay → Latin American
  "es-sv": "es-419", // El Salvador → Latin American
  "es-ni": "es-419", // Nicaragua → Latin American
  "es-cr": "es-419", // Costa Rica → Latin American
  "es-pa": "es-419", // Panama → Latin American
  "es-uy": "es-419", // Uruguay → Latin American
  "es-pr": "es-419", // Puerto Rico → Latin American
  "es-us": "es-419", // US Spanish → Latin American

  // Portuguese variants
  "pt-br": "pt-BR",
  "pt-pt": "pt-BR", // Portugal → Brazil (fallback, could add pt-PT later)

  // German variants
  "de-de": "de-DE",
  "de-at": "de-DE", // Austria → Germany
  "de-ch": "de-DE", // Switzerland → Germany

  // Danish variants
  "da-dk": "da-DK",

  // Italian variants
  "it-it": "it-IT",
  "it-ch": "it-IT", // Switzerland → Italy

  // Japanese
  "ja-jp": "ja-JP",

  // Korean
  "ko-kr": "ko-KR",

  // Chinese variants - handled specially below
  "zh-tw": "zh-TW",
  "zh-hk": "zh-TW", // Hong Kong uses Traditional
  "zh-mo": "zh-TW", // Macau uses Traditional
  "zh-hant": "zh-TW", // Traditional script
  "zh-cn": "zh-CN",
  "zh-sg": "zh-CN", // Singapore uses Simplified
  "zh-hans": "zh-CN", // Simplified script
});

/**
 * Mapping of language codes to their default two-part locale.
 * Used when we have a language code but no specific region match.
 */
const LANGUAGE_TO_DEFAULT_LOCALE = supportedLanguageByCode({
  en: "en-US",
  fr: "fr-FR",
  es: "es-419", // Default Spanish to Latin American (larger market)
  de: "de-DE",
  da: "da-DK",
  it: "it-IT",
  pt: "pt-BR",
  ja: "ja-JP",
  ko: "ko-KR",
  zh: "zh-CN",
});

/**
 * Normalizes any locale string to a supported two-part locale code.
 *
 * Handles:
 * - Legacy one-part codes (en → en-US, fr → fr-FR, etc.)
 * - Two-part codes with underscore or hyphen (en_US, en-US)
 * - Longer BCP-47 strings (zh-Hant-TW → zh-TW)
 * - Regional variants (es-MX → es-419, en-AU → en-GB)
 * - Chinese script variants (zh-Hans → zh-CN, zh-Hant → zh-TW)
 *
 * @param input - The locale string to normalize (e.g., 'en', 'en-US', 'zh-Hant-TW')
 * @returns The normalized SupportedLanguage or null if unsupported
 *
 * @example
 * normalizeToSupportedLocale('en') // → 'en-US'
 * normalizeToSupportedLocale('en-US') // → 'en-US'
 * normalizeToSupportedLocale('en-GB') // → 'en-GB'
 * normalizeToSupportedLocale('es-MX') // → 'es-419'
 * normalizeToSupportedLocale('zh-Hant-TW') // → 'zh-TW'
 * normalizeToSupportedLocale('zh-Hans') // → 'zh-CN'
 * normalizeToSupportedLocale('de') // → 'de-DE'
 */
export const normalizeToSupportedLocale = (
  input: string | null | undefined,
): SupportedLanguage | null => {
  if (!input) {
    return null;
  }

  // Normalize separators: replace underscores with hyphens
  const normalized = input.replace(/_/g, "-").toLowerCase();

  // Check if it's already a supported locale (case-insensitive match)
  const directMatch = supportedLanguages.find((lang) => lang.toLowerCase() === normalized);
  if (directMatch) {
    return directMatch;
  }

  // Check legacy one-part codes
  if (LEGACY_LOCALE_MAP[normalized]) {
    return LEGACY_LOCALE_MAP[normalized];
  }

  // Check regional variant map for exact two-part matches
  if (REGIONAL_VARIANT_MAP[normalized]) {
    return REGIONAL_VARIANT_MAP[normalized];
  }

  // Parse the locale parts
  const parts = normalized.split("-");
  const language = parts[0];

  // For longer BCP-47 strings, try to find a match with language-region
  if (parts.length >= 2) {
    const langRegion = `${parts[0]}-${parts[1]}`;
    if (REGIONAL_VARIANT_MAP[langRegion]) {
      return REGIONAL_VARIANT_MAP[langRegion];
    }
  }

  // Special handling for Chinese due to script variants
  if (language === "zh") {
    // Check if any part indicates Traditional Chinese
    const hasTraditionalIndicator = parts.some(
      (part) =>
        part === "tw" ||
        part === "hk" ||
        part === "mo" ||
        part === "hant" ||
        part === "traditional",
    );
    if (hasTraditionalIndicator) {
      return "zh-TW";
    }

    // Check if any part indicates Simplified Chinese
    const hasSimplifiedIndicator = parts.some(
      (part) => part === "cn" || part === "sg" || part === "hans" || part === "simplified",
    );
    if (hasSimplifiedIndicator) {
      return "zh-CN";
    }

    // Default Chinese to Simplified
    return "zh-CN";
  }

  // For other languages, map to default locale for that language
  if (LANGUAGE_TO_DEFAULT_LOCALE[language]) {
    return LANGUAGE_TO_DEFAULT_LOCALE[language];
  }

  // Unsupported language
  return null;
};

/**
 * Checks if a locale string is a supported language (after normalization).
 *
 * @param locale - The locale string to check
 * @returns True if the locale can be normalized to a supported language
 */
export const isSupportedLocale = (locale: string | null | undefined): boolean => {
  return normalizeToSupportedLocale(locale) !== null;
};

/**
 * The subset of supported locales that have a backend `Locale` enum equivalent and can
 * therefore be persisted to a locale setting (org supported-locales, job/employee/template
 * locale, etc.).
 *
 * Frontend-only locales — en-GB, de-DE, it-IT, en-sloth — are intentionally excluded: the
 * backend `Locale` enum has no value for them, so writing them fails server validation. Keep
 * this in sync with the backend `Locale` enum in `shared/common.graphql`.
 */
const BACKEND_SUPPORTED_LANGUAGES = new Set<SupportedLanguage>([
  "en-US",
  "fr-FR",
  "da-DK",
  "ko-KR",
  "ja-JP",
  "zh-CN",
  "zh-TW",
  "es-419",
  "es-ES",
  "pt-BR",
  "fr-CA",
]);

/**
 * Returns true when the given locale has a backend `Locale` enum equivalent, i.e. it can be
 * persisted to a locale setting. Use this to filter selectable locales (which include
 * frontend-only locales like en-sloth) down to those the backend will accept.
 */
export const isBackendSupportedLanguage = (locale: string | null | undefined): boolean => {
  const normalized = normalizeToSupportedLocale(locale);
  return normalized !== null && BACKEND_SUPPORTED_LANGUAGES.has(normalized);
};

/**
 * Converts a GraphQL Locale enum value (e.g., "EN_US") to an i18n locale code (e.g., "en-US").
 *
 * The GraphQL enum uses UPPER_SNAKE format. This function converts to the
 * lowercase-hyphenated format used by i18next, then normalizes to a supported locale.
 *
 * @returns The matching SupportedLanguage, or null if no match.
 */
export const graphqlLocaleToI18n = (
  locale: string | null | undefined,
): SupportedLanguage | null => {
  if (!locale) {
    return null;
  }
  // EN_US → en-us → normalize
  const hyphenated = locale.toLowerCase().replace(/_/g, "-");
  return normalizeToSupportedLocale(hyphenated);
};

/**
 * Converts an i18n locale code (e.g., "en-US") to a GraphQL Locale enum value (e.g., "EN_US").
 *
 * Only returns values that correspond to real GraphQL Locale enum values.
 * Frontend-only locales like "en-sloth" return null.
 *
 * @returns The GraphQL enum string, or null if the locale has no backend equivalent.
 */
export const i18nLocaleToGraphql = (locale: string | null | undefined): string | null => {
  // Normalize first, then require a backend equivalent. Frontend-only locales (en-sloth,
  // en-GB, de-DE, it-IT) have no backend `Locale` enum value and must return null rather
  // than a phantom enum string the backend would reject.
  const normalized = normalizeToSupportedLocale(locale);
  if (!normalized || !BACKEND_SUPPORTED_LANGUAGES.has(normalized)) {
    return null;
  }
  // en-US → EN_US
  return normalized.replace("-", "_").toUpperCase();
};
